import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { Logger } from "@/apis/Logger";
import { isCaptureForbidden } from "@/components/AIAutomation/capture-exclusions";
import {
  isCapturePaused,
  setCapturePaused,
} from "@/components/AIAutomation/capture-pause";
import {
  JOURNEY_CAPTURE_FLAG,
  markAiAutomationAttempted,
  markJourneySessionRecorded,
  OBSERVE_ASYNC_SUMMARIZER_FLAG,
} from "@/components/AIAutomation/constants";
import {
  type JourneyConsentState,
  mayCaptureStep,
} from "@/components/AIAutomation/journey-consent-gate";
import { getAgentCoreUrl } from "@/helpers/agentcore/agentcore-url-helper";
import { AIAgentSessionHelper } from "@/helpers/agentcore/ai-agent-session-helper";
import {
  isBearerTokenRefreshDue,
  REFRESH_DEADLINE_MS,
  TOKEN_HEARTBEAT_MS,
} from "@/helpers/agentcore/bearer-token-refresh";
import { isPermissionFlowAvailable } from "@/helpers/electron-helper";
import { SnowplowTracker } from "@/helpers/snowplow";
import { useGrantStatus } from "@/hooks/useGrantStatus";
import { refreshBearerToken } from "@/hooks/useInvokeAgent";
import { useIsAiOfferable } from "@/hooks/useIsJourneyCaptureAvailable";
import { useHiringRegion } from "@/hooks/useRegion";
import { useFeatureFlag } from "@/stores/contexts/FeatureFlagsContext";
import { type SessionObserveRequest } from "@/types/observeCapture";

/** The agent type the loop is filed under. A label, not a dispatch. */
const SESSION_AGENT_TYPE = "vpn";

/** The observe loop, and the hire's switch over it. */
export interface ObserveSession {
  /**
   * Whether a recording has been started and not yet ended. What the widget asked the host for, not
   * what the host is doing: it pauses on a locked screen and floors its cadence without saying so.
   */
  readonly isRecording: boolean;
  /**
   * Whether the hire switched capture off. Survives a refresh, and is independent of consent, which is
   * answered once and never re-asked -- so `resume` must not ask again.
   */
  readonly isPaused: boolean;
  /** Whether the session gates allow recording, apart from the pause and the held grants. */
  readonly canRecord: boolean;
  readonly pause: () => void;
  readonly resume: () => void;
}

/**
 * Autostart the session-scoped observe loop, and stop it on revocation or teardown.
 *
 * Every condition is checked here so a call site cannot apply some and not others: flag on, capture
 * permitted on the step, hiring region settled, macOS grants held, bridge present, person resolved.
 * The person matters because a journey filed under the uuid fallback is unfindable in review. The
 * region is checked again here because consent can be restored from storage before the region is
 * known.
 *
 * @param consent The session's answer to the consent question.
 * @param currentStepId The step the hire is on, or undefined when there is none to record.
 * @param start Host bridge: begins the loop. Absent on older desktop builds.
 * @param stop Host bridge: ends the loop. The host treats a stop with no loop as a no-op.
 * @param setStep Host bridge: names the step each frame is filed under.
 * @returns The loop's state, and the hire's pause switch over it. See {@link ObserveSession}.
 */
export const useObserveSession = (
  consent: JourneyConsentState,
  currentStepId: string | undefined,
  start: ((options: SessionObserveRequest) => void) | undefined,
  stop: (() => void) | undefined,
  setStep: ((stepId: string | undefined) => void) | undefined
): ObserveSession => {
  /*
   * Re-read rather than trusting the UI's availability gate: that decides whether to ask, this
   * decides whether frames are recorded, and it must refuse even if the UI misjudged.
   */
  const isEnabled = useFeatureFlag(JOURNEY_CAPTURE_FLAG);
  /*
   * Async describe cutover (NHE-6373), read on every render but only ever sent on a start, like
   * the runtime URL: the host pins it for the loop's life. Independent of `isEnabled` -- this
   * chooses HOW a permitted frame is described, not WHETHER the session runs.
   */
  const asyncObserve = useFeatureFlag(OBSERVE_ASYNC_SUMMARIZER_FLAG);
  /* An unknown region takes the US runtime, which the host then pins for the session. */
  const isRegionSettled = useIsAiOfferable();
  /*
   * Recording waits for the macOS grants, not just for consent.
   *
   * Started on consent alone, the loop's first capture runs while the hire is still working through
   * the permissions walkthrough -- and macOS answers that with a permission dialog of its own, which
   * arrives unexplained and before they have pressed anything. A build that cannot read the grants is
   * not held to them, or an older desktop app would lose recording it does today.
   */
  const { areGrantsHeld, didProbeAnswer, missingGrants } = useGrantStatus();
  /*
   * Kept out of `canRecord` so the two stay separable. Persisted, so a hire who switched capture off
   * does not find it back on after a refresh or a relaunch. No surface reads either one today.
   */
  const personId = SnowplowTracker.userId;
  const [isPaused, setIsPaused] = useState(() => isCapturePaused(personId));
  /*
   * Read again for each person this page resolves. The hook mounts before sign-in, and the id then
   * changes twice: `useLoadRegion` sets it from the Cognito user, and `useUserProgress` overwrites it
   * with the canonical `personId` -- which differ for a hire who changed their PPv2 email. Keyed on
   * "have we read at all" rather than on who, the canonical hire's stored pause is never looked for.
   *
   * Only ever adopts a pause, never lifts one: a hire who switched capture off in this session must not
   * have that undone by a stored answer arriving late. A pause taken before their id was known is
   * written out here instead, since the record it should have gone into did not exist yet.
   */
  const readPauseForPerson = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (personId === undefined || readPauseForPerson.current === personId) {
      return;
    }
    readPauseForPerson.current = personId;
    if (isCapturePaused(personId)) {
      setIsPaused(true);
      return;
    }
    if (isPaused) {
      setCapturePaused(personId, true);
    }
  }, [isPaused, personId]);
  const setPaused = useCallback(
    (paused: boolean): void => {
      setCapturePaused(personId, paused);
      setIsPaused(paused);
    },
    [personId]
  );
  const pause = useCallback((): void => setPaused(true), [setPaused]);
  const resume = useCallback((): void => setPaused(false), [setPaused]);

  // `mayCaptureStep` owns every step rule, including that an unknown step is forbidden.
  const mayCaptureThisStep = mayCaptureStep(consent, currentStepId);
  /*
   * "Nothing is known to be missing", not "a probe confirmed they are held", so a probe that merely
   * failed does not look like a refusal. `canRecord` is its only consumer, and `mayStart` then adds
   * the stricter `areGrantsHeld`, so the looseness alone cannot start a recording. No surface reads
   * `canRecord` today.
   */
  const areGrantsNotKnownMissing =
    !isPermissionFlowAvailable() || missingGrants.length === 0;
  const canRecord =
    isEnabled &&
    isRegionSettled &&
    areGrantsNotKnownMissing &&
    start !== undefined &&
    mayCaptureThisStep;
  const mayStart = canRecord && areGrantsHeld && !isPaused;

  /*
   * Not simply `!mayStart`: the probe re-runs on every window focus and reports a rejection as no
   * answer, so one IPC hiccup would cut a live recording. Not knowing is not being refused.
   */
  const hasLostAGrant =
    isPermissionFlowAvailable() && didProbeAnswer && missingGrants.length > 0;
  /* The hire's pause belongs here too: switching capture off has to end the loop it is running. */
  const mustStop =
    !isEnabled || isPaused || hasLostAGrant || !mayCaptureThisStep;

  // Kept apart from the other reasons the loop may not run: it is the only one the host can
  // enforce itself, and so the only stop worth a word to the host first.
  const isOnForbiddenStep =
    currentStepId !== undefined && isCaptureForbidden(currentStepId);

  /*
   * Read on every render but only ever sent on a start, because the host pins it for the loop's
   * life and re-pointing a running session would split its frames across two regions.
   */
  const hiringRegion = useHiringRegion();
  const agentCoreUrl = getAgentCoreUrl(hiringRegion);

  /*
   * Two facts, not one latch: `hasPendingStart` covers a start from the moment it is
   * claimed (so a re-render cannot dispatch a second loop), `isRecording` only once the
   * host has been told to start. Stop paths check both as defence in depth; they cannot
   * currently disagree.
   */
  const hasPendingStart = useRef(false);
  const isRecording = useRef(false);
  const mounted = useRef(true);

  /*
   * The recording fact is held twice on purpose. The ref is what the heartbeat and the effect bodies
   * below read, where a render-scoped value would be stale; the state is the only form a render can
   * see. No render reads it today.
   */
  const [isRecordingNow, setIsRecordingNow] = useState(false);
  const markRecording = useCallback((recording: boolean): void => {
    isRecording.current = recording;
    // Every stop path runs through a layout-effect cleanup, where a state write is discarded anyway.
    if (mounted.current) {
      setIsRecordingNow(recording);
    }
  }, []);

  /*
   * Bumped when a claim is dropped, so a start still awaiting its token stands down rather
   * than dispatching a second loop -- two loops means two heartbeats and one handle.
   */
  const startGeneration = useRef(0);

  const tokenHeartbeat = useRef<ReturnType<typeof setInterval>>();
  const tokenExpiresAtSeconds = useRef<number | undefined>(undefined);
  const isRefreshInFlight = useRef(false);
  const lastRefreshedAtMs = useRef(0);
  const refreshAttempt = useRef(0);

  /*
   * Layout effect, not passive: React 17 defers passive effects to a Scheduler task, so a
   * promise landing in between would read the previous render's decision.
   */
  const latest = useRef({
    mayStart,
    stepId: currentStepId,
    agentCoreUrl,
    asyncObserve,
  });
  useLayoutEffect(() => {
    latest.current = {
      mayStart,
      stepId: currentStepId,
      agentCoreUrl,
      asyncObserve,
    };
  }, [mayStart, currentStepId, agentCoreUrl, asyncObserve]);

  /*
   * Read through a ref so the teardown can depend on nothing: keyed on `stop`, a bridge
   * arriving late would look like an unmount and stop a loop mid-session.
   */
  const stopRef = useRef(stop);
  useLayoutEffect(() => {
    stopRef.current = stop;
  }, [stop]);

  /**
   * Refresh the token, giving up on the wait at the deadline. Both callers go through here
   * so there is one in-flight refresh and neither can wait forever.
   */
  const refreshTokenBounded = useCallback(async (): Promise<
    number | undefined
  > => {
    isRefreshInFlight.current = true;
    const generation = startGeneration.current;
    /*
     * The deadline releases the in-flight claim so the next beat can retry, which means an
     * abandoned call can still be running when a later one starts. It cannot be cancelled,
     * so it is identified instead: only the newest attempt may stamp the floor.
     */
    const attempt = refreshAttempt.current + 1;
    refreshAttempt.current = attempt;
    let giveUpWaiting: ReturnType<typeof setTimeout> | undefined;
    try {
      return await Promise.race([
        refreshBearerToken().then((expiresAtSeconds) => {
          // Only a real completion stamps the floor, and only the newest attempt of the
          // live recording: a stamp from an abandoned or ended one would hold back the
          // next legitimate refresh, and the floor outranks the expiry.
          if (
            generation === startGeneration.current &&
            attempt === refreshAttempt.current
          ) {
            lastRefreshedAtMs.current = Date.now();
          }
          return expiresAtSeconds;
        }),
        new Promise<undefined>((resolve) => {
          giveUpWaiting = setTimeout(
            () => resolve(undefined),
            REFRESH_DEADLINE_MS
          );
        }),
      ]);
    } finally {
      // The race's loser stays armed otherwise, one stray timer per refresh.
      clearTimeout(giveUpWaiting);
      isRefreshInFlight.current = false;
    }
  }, []);

  /** A host call must not take the page down with it, whatever the bridge does. */
  const callBridge = useCallback((what: string, invoke: () => void): void => {
    try {
      invoke();
    } catch (error) {
      void Logger.errorErrorLike(`Observe session: host ${what} threw`, error);
    }
  }, []);

  const clearTokenHeartbeat = useCallback((): void => {
    if (tokenHeartbeat.current !== undefined) {
      clearInterval(tokenHeartbeat.current);
      tokenHeartbeat.current = undefined;
    }
  }, []);

  /** Every stop path goes through here, so none can leave the heartbeat running. */
  const endRecording = useCallback(
    (stopLoop: (() => void) | undefined): void => {
      clearTokenHeartbeat();
      tokenExpiresAtSeconds.current = undefined;
      lastRefreshedAtMs.current = 0;
      isRefreshInFlight.current = false;
      startGeneration.current += 1;
      try {
        callBridge("stop", () => stopLoop?.());
      } finally {
        // Cleared even if the bridge threw, or a stale claim blocks every later start.
        hasPendingStart.current = false;
        markRecording(false);
      }
    },
    [callBridge, clearTokenHeartbeat, markRecording]
  );

  /*
   * The loop outlives its token -- two hours from the API's Cognito client, and sessions
   * run longer. Nothing else renews it, so past expiry every turn 401s while the host
   * keeps capturing and nothing reports the truncation.
   */
  const keepTokenFresh = useCallback(
    (expiresAtSeconds: number | undefined): void => {
      // Idempotent: assigning over a live handle would orphan an unreachable timer.
      clearTokenHeartbeat();
      tokenExpiresAtSeconds.current = expiresAtSeconds;
      tokenHeartbeat.current = setInterval(() => {
        // A net, not a path: unreachable while `endRecording` owns the only handle.
        /* istanbul ignore next */
        if (!isRecording.current) return;
        // One at a time: the expiry is written only on resolve, so a stall would start
        // another every beat and the last to land could install an older token.
        if (isRefreshInFlight.current) return;
        if (
          !isBearerTokenRefreshDue(
            tokenExpiresAtSeconds.current,
            lastRefreshedAtMs.current
          )
        ) {
          return;
        }
        const generation = startGeneration.current;
        void (async () => {
          try {
            const nextExpiresAtSeconds = await refreshTokenBounded();
            // A refresh outliving its recording must not overwrite a live expiry.
            if (generation !== startGeneration.current) return;
            tokenExpiresAtSeconds.current = nextExpiresAtSeconds;
          } catch (error) {
            void Logger.errorErrorLike(
              "Observe session: mid-session bearer refresh failed",
              error
            );
          }
        })();
      }, TOKEN_HEARTBEAT_MS);
    },
    [clearTokenHeartbeat, refreshTokenBounded]
  );

  /*
   * A reload or cross-document navigation skips React cleanup, leaving the host's
   * deadline-free loop with nothing to stop it -- the next page's latches start clear. So
   * stop on pagehide, and claim any inherited loop once on mount.
   *
   * A renderer crash fires neither, so nothing here can end that loop -- the host's crash hook
   * stops it instead, which is why this only has to cover the paths a live renderer takes.
   *
   * Flag-gated as well as bridge-gated, so a hire outside the rollout is sent nothing. An
   * orphan can only exist where the flag was on, at the cost of waiting for AppConfig.
   */
  const hasReaped = useRef(false);
  useEffect(() => {
    if (!isEnabled || !stop) return;
    if (!hasReaped.current) {
      hasReaped.current = true;
      callBridge("stop", stop);
    }
    const stopOnPageHide = (): void => endRecording(stop);
    window.addEventListener("pagehide", stopOnPageHide);
    return () => window.removeEventListener("pagehide", stopOnPageHide);
  }, [isEnabled, stop, endRecording, callBridge]);

  useLayoutEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      if (hasPendingStart.current || isRecording.current) {
        endRecording(stopRef.current);
      }
    };
  }, [endRecording]);

  useEffect(() => {
    // Stops as well as declines: the hire can revoke or finish while it is running.
    if (mustStop && (hasPendingStart.current || isRecording.current)) {
      if (isOnForbiddenStep) {
        // Named before the stop, never instead of it: the stop is fire-and-forget, and a host
        // that never receives it can still refuse the frames if it knows the step.
        callBridge("setStep", () => setStep?.(currentStepId));
      }
      endRecording(stop);
      return;
    }
    if (!mayStart) return;
    if (hasPendingStart.current || !start) return;
    if (!AIAgentSessionHelper.hasResolvedActor()) return;

    const identity = AIAgentSessionHelper.createSession();
    // Claimed before the await so a re-render mid-refresh cannot start a second loop.
    hasPendingStart.current = true;
    const generation = startGeneration.current;

    const startOnceTokenIsFresh = async (): Promise<void> => {
      let expiresAtSeconds: number | undefined;
      try {
        // Bounded, or a stall holds this claim for the rest of the page and the session
        // records nothing -- worse than recording on the token the host already holds.
        expiresAtSeconds = await refreshTokenBounded();
      } catch (error) {
        // Starting anyway beats no record; the first heartbeat learns the real expiry.
        void Logger.errorErrorLike(
          "Observe session: bearer refresh failed; starting anyway",
          error
        );
      }

      const {
        mayStart: stillAllowed,
        stepId,
        agentCoreUrl: runtimeUrl,
        asyncObserve: asyncObserveEnabled,
      } = latest.current;
      if (!mounted.current || !stillAllowed) {
        /*
         * Released here, because nothing else will. The stop path reaps a claim only when the loop
         * `mustStop`, and a probe that merely stopped answering clears `mayStart` without that -- so a
         * start abandoned in between would hold the claim for the rest of the page and the session
         * would never record. Only the attempt that still owns the claim may drop it.
         */
        if (generation === startGeneration.current) {
          hasPendingStart.current = false;
        }
        return;
      }
      // Superseded while waiting, so a newer attempt owns the claim now.
      if (generation !== startGeneration.current) return;

      let hasStarted = false;
      callBridge("start", () => {
        start({
          ...identity,
          agentType: SESSION_AGENT_TYPE,
          ...(runtimeUrl && { agentCoreUrl: runtimeUrl }),
          ...(asyncObserveEnabled && { asyncObserve: true }),
        });
        hasStarted = true;
      });
      if (!hasStarted) {
        // Left claimed, the gate blocks every later attempt for the rest of the page.
        hasPendingStart.current = false;
        return;
      }
      markRecording(true);
      /*
       * Set on the start, not on consent: together they offer an engineer a link to this recording,
       * and consent alone does not mean one exists. Stamped with the actor the frames are filed
       * under rather than the logged person, which is read later and can have changed by then.
       */
      markAiAutomationAttempted();
      markJourneySessionRecorded(identity.actorId);
      // After starting, because the host resets the label there. From `latest`, not the
      // closure, or the frame carries the step this effect was scheduled under.
      callBridge("setStep", () => setStep?.(stepId));
      keepTokenFresh(expiresAtSeconds);
    };
    void startOnceTokenIsFresh();

    // currentStepId stays in the deps so a render where the actor has since resolved
    // retries. Safe to re-run: the claim absorbs the repeat.
  }, [
    mayStart,
    mustStop,
    currentStepId,
    isOnForbiddenStep,
    start,
    stop,
    setStep,
    endRecording,
    keepTokenFresh,
    callBridge,
    refreshTokenBounded,
    markRecording,
  ]);

  /*
   * Frames are unattributed until a step is named, and the host discards one sent in the
   * start payload. Re-checks permission so naming never depends on effect order.
   *
   * Keyed on `mustStop` rather than `mayStart`, because the two are not opposites: a probe that stops
   * answering clears `mayStart` while deliberately leaving the recording open, and gating on it there
   * would file every later step's frames under the step the hire was on when the probe faltered.
   */
  useEffect(() => {
    /* Keyed on the stop gate, not the start gate: a recording that outlives a failed probe must keep
       naming its step, or its frames arrive unattributed. */
    if (!isRecording.current || mustStop) return;
    callBridge("setStep", () => setStep?.(currentStepId));
  }, [currentStepId, mustStop, setStep, callBridge]);

  return useMemo(
    () => ({ canRecord, isPaused, isRecording: isRecordingNow, pause, resume }),
    [canRecord, isPaused, isRecordingNow, pause, resume]
  );
};
